import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  IonText
} from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { isNil, map } from 'lodash';
import { Subject, finalize, mergeMap, of, takeUntil, tap } from 'rxjs';
import { PRODUCT } from '@components/in-app-purchase/in-app-purchase.enum';
import { AppSubscription, SubscriptionQueryResult } from '@interfaces/schema';
import { assistantSubscriptionActions } from '@pages/assistant/subscription/subscription.actions';
import {
  selectAssistantProductSubscriptionsQuery,
  selectAssistantSubscriptionCheck360Report
} from '@pages/assistant/subscription/subscription.selectors';
import { AssistantSubscriptionState } from '@pages/assistant/subscription/subscription.state';
import { CdnPipe } from '@pipes/cdn/cdn.pipe';
import { CurrencyPipe } from '@pipes/currency/currency';
import { PLANS } from './list-plan.data';
import { AssistantSubscriptionPlanItem } from './list-plan.interface';
import { SubscriptionListPlanService } from './list-plan.service';

type AppSubscriptionWithParsedPlans = AppSubscription & {
  parsedPlans: Array<string>;
  parsedTags: Array<{
    text: string;
    type: string;
  }>;
};

/**
 * List subscription plan component of asax assistant.
 */
@Component({
  selector: 'app-assistant-subscription-list-plan',
  templateUrl: './list-plan.component.html',
  styles: [
    `
      * {
        @apply font-inter;
      }
      .plan {
        @apply px-4 py-4 bg-white;

        border-radius: 10px;
        position: relative;
        background-clip: padding-box;
        border: 1px solid transparent;
        box-sizing: border-box;

        &::before {
          content: '';
          @apply absolute top-0 left-0 right-0 bottom-0;
          z-index: -1;
          margin: -1px;
          border-radius: inherit;
          background: var(--ion-color-main-button);
        }
      }
      .discount {
        background: var(--ion-color-pink);
        color: #ffffff;
      }
      .best-plan {
        background: var(--ion-color-main-button);
        color: #ffffff;
      }
      .text-plan {
        .discount {
          background: transparent;
          color: var(--ion-color-pink);
        }

        .best-plan {
          background: transparent;
          color: var(--ion-color-purple);
        }
      }
    `
  ],
  imports: [
    CommonModule,
    IonContent,
    IonText,
    IonIcon,
    IonSkeletonText,
    IonModal,
    IonList,
    IonItem,
    IonLabel,
    IonSegment,
    IonSegmentButton,
    CurrencyPipe,
    CdnPipe
  ],
  providers: [SubscriptionListPlanService],
  host: {
    '[class.block]': 'true'
  }
})
export class AssistantSubscriptionListPlanComponent
  implements OnInit, OnDestroy
{
  private readonly sanitizer = inject(DomSanitizer);
  private readonly service = inject(SubscriptionListPlanService);
  private readonly store = inject(Store);

  readonly ableToSelectPlan = input(true);

  readonly loadingAvailablePlanChanged = output<boolean>();
  readonly planClicked = output();
  readonly selectedPlanStoreProductIdChange = output<string>();

  private readonly check360Query = signal<
    AssistantSubscriptionState['check360ReportQuery']
  >({
    error: undefined,
    loading: false,
    result: {
      hasActivated: false,
      hasPurchased: false,
      hasReleased: false,
      ableToUnlock: false
    },
    queryCount: 0
  });
  private readonly defaultPlanIdHasBenSet: WritableSignal<boolean> =
    signal<boolean>(false);
  protected readonly isLoadingAvailablePlans: WritableSignal<boolean> =
    signal<boolean>(true);
  private readonly loadingFetch: WritableSignal<boolean> =
    signal<boolean>(false);
  protected readonly openPlan: WritableSignal<boolean> = signal<boolean>(false);
  private readonly plans = signal<Array<AssistantSubscriptionPlanItem>>(
    PLANS.map((item) => ({
      ...item,
      title: this.sanitizer.bypassSecurityTrustHtml(item.title) as string
    }))
  );
  private readonly productSubscriptionsQuery = signal<{
    loading: AssistantSubscriptionState['productSubscriptionsQuery']['loading'];
    items: AssistantSubscriptionState['productSubscriptionsQuery']['result']['items'];
    queryCount: AssistantSubscriptionState['productSubscriptionsQuery']['queryCount'];
  }>({
    items: [],
    loading: false,
    queryCount: 0
  });
  public readonly selectedPlan = signal<AppSubscriptionWithParsedPlans | null>(
    null
  );
  public readonly subscriptionPlans = signal<
    Array<AppSubscriptionWithParsedPlans>
  >([]);

  selectedPlanCode = model<string | null>(null);
  selectedPlanId = model<string | null>(null);

  destroyed$ = new Subject<void>();

  ableToPurchase360 = computed<boolean>(() => {
    const txyStatus = this.check360Query();

    if (
      txyStatus.result.hasActivated ||
      txyStatus.result.hasReleased ||
      txyStatus.result.hasPurchased
    )
      return false;
    return true;
  });

  /**
   * Get daily price from selected plan.
   *
   * @returns {number} Daily price.
   */
  dailyPrice = computed<number>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return plan.dailyPrice ?? 0;
    }
    return 0;
  });

  /**
   * Get discount text from selected plan.
   *
   * @returns {string} Discount text.
   */
  discountText = computed<string>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return plan.parsedTags.find((val) => val.type === 'discount')?.text ?? '';
    }
    return '';
  });

  isLoading = computed(() => {
    if (this.check360Query().loading && this.check360Query().queryCount < 1)
      return true;
    if (
      this.productSubscriptionsQuery().loading &&
      this.productSubscriptionsQuery().queryCount < 1
    )
      return true;
    return false;
  });

  displayedPlans = computed(() => {
    const plans = this.plans();
    const txyStatus = this.check360Query();
    const productSubscription = this.productSubscriptionsQuery();
    const isLoading = this.isLoading();
    if (this.ableToPurchase360() && !isLoading) {
      const txyPlanIndex = plans.findIndex(
        (item) => item.id === 'asax_assistant_360_dna'
      );
      let productSubIndex = -1;
      if (txyStatus.result.ableToUnlock) {
        productSubIndex = productSubscription.items.findIndex(
          (item) => item.code === PRODUCT.UNLOCK_ASAX_ASSISTANT_360_DNA
        );
      } else {
        productSubIndex = productSubscription.items.findIndex(
          (item) => item.code === PRODUCT.PURCHASE_ASAX_ASSISTANT_360_DNA
        );
      }

      if (txyPlanIndex >= 0) {
        let price = plans[txyPlanIndex].price;
        if (productSubIndex >= 0) {
          price = productSubscription.items[productSubIndex].price ?? price;
        }
        plans[txyPlanIndex].price = price;
      }
      return plans;
    }

    return plans.filter((item) => item.id !== 'asax_assistant_360_dna');
  });

  /**
   * Check if selected plan has discount.
   *
   * @returns {boolean} Has discount.
   */
  isDiscount = computed<boolean>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return plan.totalDiscount ? plan.totalDiscount !== 0 : false;
    }
    return false;
  });

  /**
   * Get type of plan text from selected plan.
   *
   * @returns {string} Type of plan.
   */
  planTypeText = computed<string>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return this.typePlan(plan.code ?? '');
    }
    return '';
  });

  /**
   * Get price of selected plan.
   *
   * @returns {number} Price.
   */
  price = computed<number>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return plan.price ?? 0;
    }
    return 0;
  });

  /**
   * Get total daily price of selected plan.
   *
   * @returns {number} Total daily price.
   */
  totalDailyPrice = computed<number>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return plan.totalDailyPrice ?? 0;
    }
    return 0;
  });

  /**
   * Get total price of selected plan.
   *
   * @returns {number} Total price.
   */
  totalPrice = computed<number>(() => {
    const plan = this.selectedPlan();
    if (plan) {
      return plan.totalPrice ?? 0;
    }
    return 0;
  });

  /**
   * Create new instance.
   */
  constructor() {
    effect(() => {
      // const plans = this.displayedPlans();
      const plans = this.subscriptionPlans();
      if (
        plans.length > 0 &&
        this.selectedPlanId() === null &&
        !this.defaultPlanIdHasBenSet()
      ) {
        this.defaultPlanIdHasBenSet.set(true);
        this.selectPlan(plans[0]);
      }
    });
  }

  /**
   * On component initialized.
   */
  ngOnInit(): void {
    this.store
      .select(selectAssistantProductSubscriptionsQuery)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((v) => {
        this.productSubscriptionsQuery.set({
          items: v.result.items,
          loading: v.loading,
          queryCount: v.queryCount
        });
        this.isLoadingAvailablePlans.set(this.isLoading());
      });
    this.store.dispatch(assistantSubscriptionActions.check360ReportStatus());
    this.store
      .select(selectAssistantSubscriptionCheck360Report)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((query) => {
        this.check360Query.set(query);
        this.isLoadingAvailablePlans.set(this.isLoading());
        this.loadingAvailablePlanChanged.emit(this.isLoadingAvailablePlans());
      });
    this.fetchSubscriptionPlans();
  }

  /**
   * On component destroyed.
   */
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Get subscription plans.
   */
  fetchSubscriptionPlans(): void {
    of(true)
      .pipe(
        tap(() => {
          this.loadingFetch.set(true);
        })
      )
      .pipe(mergeMap(() => this.service.fetch()))
      .pipe(
        finalize(() => {
          this.loadingFetch.set(false);
        })
      )
      .subscribe({
        /**
         * Handle response resolve.
         *
         * @param {SubscriptionQueryResult} resolve Resolve.
         */
        next: (resolve: SubscriptionQueryResult) => {
          const { results } = resolve;
          this.setSubscriptionPlans(results);
        }
      });
  }

  /**
   * Select plan.
   *
   * @param {AppSubscriptionWithParsedPlans} item Item.
   */
  selectPlan(item: AppSubscriptionWithParsedPlans): void {
    this.planClicked.emit();
    if (!this.ableToSelectPlan()) return;
    if (this.selectedPlanId() && this.selectedPlanId() === item.id) {
      // do nothing
    } else {
      const storeProductId = item.productId as string;
      this.selectedPlanId.set(item.id as string);
      this.selectedPlanCode.set(item.code as string);
      this.selectedPlanStoreProductIdChange.emit(storeProductId);
      this.selectedPlan.set(item);

      if (this.openPlan()) {
        this.openPlan.set(false);
      }
    }
  }

  /**
   * Set subscription plans by 360 ableToUnlock condition.
   * Also parse any stringify arrays/objects.
   *
   * @param {Array<AppSubscription> | undefined} results Fetched subscription plans.
   * @returns {void}
   */
  setSubscriptionPlans(results: Array<AppSubscription> | undefined): void {
    this.subscriptionPlans.set([]);
    if (results && results.length > 0) {
      const txyStatus = this.check360Query();
      const isLoading = this.isLoading();
      if (this.ableToPurchase360() && !isLoading) {
        // if ableToUnlock, remove regular 360 subscription
        // otherwise, remove unlock 360 subscription
        let removedItemIndex;
        if (txyStatus.result.ableToUnlock) {
          removedItemIndex = results.findIndex((item) =>
            item.code?.includes('asax_assistant_360_dna')
          );
        } else {
          removedItemIndex = results.findIndex((item) =>
            item.code?.includes('asax_assistant_unlock_360_dna')
          );
        }
        if (removedItemIndex !== -1) {
          results.splice(removedItemIndex, 1);
        }
      } else {
        results = results.filter((item) => !item.code?.includes('360_dna'));
      }

      const withParsedPlans = map(results, (result) => ({
        ...result,
        parsedPlans: isNil(result.planIncludes)
          ? []
          : JSON.parse(result.planIncludes),
        parsedTags: isNil(result.tags) ? [] : JSON.parse(result.tags)
      }));

      this.subscriptionPlans.set(withParsedPlans);

      if (withParsedPlans.length > 0) {
        this.selectPlan(withParsedPlans[0]);
        this.selectedPlan.set(withParsedPlans[0]);
      }
    }
  }

  /**
   * Get type of plan from code.
   *
   * @param {string} code Code.
   * @returns {string} Type of plan.
   */
  typePlan(code: string): string {
    if (code) {
      if (code.includes('month')) {
        return 'month';
      }

      if (code.includes('year')) {
        return 'year';
      }

      if (code.includes('day')) {
        return 'day';
      }
    }
    return '';
  }
}
